import { useCallback, useRef } from 'react';
import { useSetAtom } from 'jotai';
import { useFrameDecoder } from '../../../common/hooks/use-frame-decoder';
import type { WebWorker } from '../../../workers/types/common';
import { dispatchAtom } from '../atoms/label-store-dispatch/dispatch';
import { EVENTS } from '../atoms/label-store-dispatch/events';
type UseThumbnailQueuePropsT = {
  videoFrameProcessor?: WebWorker;
};
export function useThumbnailQueue({
  videoFrameProcessor
}: UseThumbnailQueuePropsT) {
  const {
    decodeFrame
  } = useFrameDecoder({
    videoFrameProcessor
  });
  const dispatch = useSetAtom(dispatchAtom);
  const sharedCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const pendingFramesRef = useRef<Set<number>>(new Set());
  const isProcessingRef = useRef<boolean>(false);
  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || pendingFramesRef.current.size === 0) {
      return;
    }
    isProcessingRef.current = true;
    const frameIndex = pendingFramesRef.current.values().next().value;
    pendingFramesRef.current.delete(frameIndex);
    let imageBitmap: ImageBitmap | null = null;
    try {
      imageBitmap = await decodeFrame(frameIndex);
      const canvas = sharedCanvasRef.current;
      canvas.width = imageBitmap.width;
      canvas.height = imageBitmap.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        imageBitmap.close();
        imageBitmap = null;
        return;
      }
      ctx.drawImage(imageBitmap, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      imageBitmap.close();
      imageBitmap = null;
      dispatch({
        type: EVENTS.SET_FRAME_IMAGE_URL,
        frameIndex,
        imageUrl: dataUrl
      });
    } catch (error) {
      console.error('[useThumbnailQueue] Error decoding frame:', frameIndex, error);
    } finally {
      // Ensure imageBitmap is closed even if an error occurred
      if (imageBitmap) {
        imageBitmap.close();
      }
      isProcessingRef.current = false;
      processQueue();
    }
  }, [decodeFrame, dispatch]);
  const enqueueFrame = useCallback((frameIndex: number) => {
    pendingFramesRef.current.add(frameIndex);
    if (!isProcessingRef.current) {
      processQueue();
    }
  }, [processQueue]);
  return {
    enqueueFrame
  };
}